/**
 * « Le travail du jour » — le deuxième écran, celui qui sert les exercices.
 *
 * Demandé par Hedi le 2 septembre 2026 : « après, l'élève a l'écran avec les
 * exercices du jour en fonction de : les matières de demain, celles vues
 * aujourd'hui et les examens qu'il a les prochains jours. Fais en sorte que ça
 * soit configurable par l'élève […] configurable au niveau global pour l'élève,
 * pas par jour. Garder aussi la possibilité de travailler une matière en
 * particulier (réservé aux matières principales). »
 *
 * Quatre choses en découlent, et chacune se lit dans le code ci-dessous.
 *
 * **Le cap est un réglage, pas une question du soir.** Il est demandé une fois,
 * il vaut tous les jours, et il se change ici même. Reposer la question chaque
 * soir aurait fait de l'écran d'exercices un formulaire à trois cases avant le
 * premier énoncé — et un enfant qui doit choisir sa stratégie de travail tous
 * les jours choisit surtout de fermer l'application.
 *
 * **Le repli s'annonce.** « Ce que j'ai demain » n'a de sens que si l'emploi du
 * temps de demain est connu ; il ne l'est pas toujours (vacances, synchro en
 * retard, vendredi soir). Le serveur descend alors vers le cap qui demande le
 * moins de données, et l'écran dit lequel il a servi. Servir autre chose que ce
 * qu'annonce le réglage, sans le dire, est la seule chose que l'enfant peut
 * constater lui-même — et la seule qui lui apprendrait à ne plus croire l'écran.
 *
 * **Le choix libre est borné aux matières principales**, telles qu'un parent les
 * a fixées (`teacher-people set-main-subjects`). Ce n'est pas une permission
 * technique : c'est ce qui empêche une soirée entière de passer sur la matière
 * préférée pendant que celle qui coince attend.
 *
 * **Aucun score** (invariant §14.1) : le nombre affiché est une quantité
 * d'exercices restants, jamais une réussite.
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { call } from "@/api/client";
import type {
  CompetenceARevoir,
  EtapeDePositionnement,
  ExerciseOut,
  TravailDuJour,
} from "@/api/schema";
import { useSession } from "@/app/session";
import { CahierDeLaSemaine } from "@/child/CahierDeLaSemaine";
import { DevoirsDuCahier } from "@/child/DevoirsDuCahier";
import { Echeances } from "@/child/Echeances";
import { EntrainementDuParent } from "@/child/EntrainementDuParent";
import { FicheDuCarnet } from "@/child/FicheDuCarnet";
import { SujetsDuParent } from "@/child/SujetsDuParent";
import { ExerciceEtCorrige } from "@/child/ExerciceEtCorrige";
import { enClairCourt } from "@/ui/dates";
import { etiquette } from "@/ui/matieres";
import { phraseDuManque } from "@/ui/penurie";
import { phraseSansPreuve, separerParJour } from "@/ui/planDuSoir";
import { Button, Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";

type Cap = TravailDuJour["focus"];

const CAPS: { valeur: Cap; libelle: string }[] = [
  { valeur: "aujourdhui", libelle: "Ce que j'ai vu aujourd'hui" },
  { valeur: "demain", libelle: "Ce que j'ai demain" },
  { valeur: "examen", libelle: "Mon prochain contrôle" },
];

type Etat =
  | { quoi: "chargement" }
  | { quoi: "panne" }
  | { quoi: "plan"; plan: TravailDuJour; matiere: string | null }
  // Épuisé n'est pas une panne : un sujet sans exercice disponible est un état
  // normal, et le peindre en rouge apprend à l'enfant qu'il a cassé quelque chose.
  | { quoi: "epuise"; plan: TravailDuJour; matiere: string | null; sujet: string }
  | {
      quoi: "exercice";
      plan: TravailDuJour;
      matiere: string | null;
      sur: CompetenceARevoir;
      item: ExerciseOut;
    };

export function TravailScreen() {
  const { profile } = useSession();
  const navigate = useNavigate();
  const [etat, setEtat] = useState<Etat>({ quoi: "chargement" });

  const charger = useCallback(async (matiere: string | null = null) => {
    setEtat({ quoi: "chargement" });
    try {
      const plan = matiere
        ? await call("travailDuneMatiere", { params: { matiere } })
        : await call("travailDeLaJournee");
      setEtat({ quoi: "plan", plan, matiere });
    } catch {
      setEtat({ quoi: "panne" });
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  const changerDeCap = async (focus: Cap) => {
    setEtat({ quoi: "chargement" });
    try {
      await call("choisirMonCap", { body: { focus } });
    } catch {
      // Le réglage n'a pas pris ; le plan du jour, lui, reste servable. On
      // recharge quand même plutôt que de laisser l'écran sur un chargement.
    }
    await charger();
  };

  const commencer = async (
    plan: TravailDuJour,
    matiere: string | null,
    sur: CompetenceARevoir,
  ) => {
    // **Une compétence en série ne se tire pas ici.** Un flash n'est un objet
    // pédagogique que dans son dispositif — une série sur une même compétence,
    // un chronomètre annoncé et visible (`exercise` §10) —, et cet écran n'a
    // ni l'un ni l'autre à offrir ; depuis le 10 septembre 2026 la route du soir rend 404
    // sur une compétence qui n'a que ça.
    //
    // **Le serveur dit laquelle** (`Competence.serie`), et rien n'est redérivé
    // ici : la cascade « quelle forme pour cette compétence » a déjà été
    // réécrite trois fois dans ce dépôt, et une quatrième copie dans une
    // coquille qui ne voit pas le pool aurait envoyé vers une série inexistante
    // ou retenu une compétence qui en a une.
    if (sur.serie) {
      navigate(`/eleve/flash/${encodeURIComponent(sur.competency_id)}`, {
        state: { retour: "/eleve/travail" },
      });
      return;
    }
    setEtat({ quoi: "chargement" });
    try {
      const item = await call("exerciceDuJour", { body: { competency_id: sur.competency_id } });
      setEtat({ quoi: "exercice", plan, matiere, sur, item });
    } catch {
      // Le pool a pu se vider entre le plan et le tirage — le délai de repos
      // d'un exercice court pendant qu'on lit l'écran.
      setEtat({ quoi: "epuise", plan, matiere, sujet: sur.libelle });
    }
  };

  if (etat.quoi === "chargement") return <Loading label="Je prépare ton travail…" />;

  if (etat.quoi === "panne") {
    return (
      <Screen title="Le travail du jour">
        <ErrorNote>Je n'arrive pas à préparer ton travail.</ErrorNote>
        <Button onClick={() => void charger()}>Réessayer</Button>
      </Screen>
    );
  }

  if (etat.quoi === "exercice") {
    return (
      <Screen title={etiquette(etat.sur.matiere)}>
        <ExerciceEtCorrige
          item={etat.item}
          matiere={etat.sur.matiere}
          retour={() => void charger(etat.matiere)}
          suivant={() => void commencer(etat.plan, etat.matiere, etat.sur)}
        />
      </Screen>
    );
  }

  const { plan, matiere } = etat;
  const competences = plan.competences ?? [];
  const manque = plan.manque ?? [];
  const { dabord, ensuite } = separer(plan);

  return (
    <Screen title="Le travail du jour" lead={annonce(plan, matiere)}>
      {/* Devant tout le reste, y compris le cahier de textes : ce qu'un parent a
          coché exercice par exercice dans un cahier qu'il a sous la main (Hedi,
          24 septembre 2026). C'est la seule carte de l'écran qui attende
          quelque chose en retour ce soir, et elle n'occupe rien les soirs où
          elle n'existe pas — donc la placer là ne repousse jamais le devoir du
          professeur vers le bas sans raison. */}
      <EntrainementDuParent />
      {/* La fiche papier du soir, quand un carnet de la semaine a été imprimé :
          même nature — un travail demandé par un adulte, rendu en photo — et
          rien quand il n'y en a pas. */}
      <FicheDuCarnet />
      {/* Un sujet entier donné par un parent : même nature que la carte
          précédente — un travail demandé par un adulte, qui attend une copie en
          retour —, et rien quand il n'y en a pas. */}
      <SujetsDuParent />

      {/* En tête, et avant toute proposition de notre part : ce que les
          professeurs ont écrit. C'est la seule carte de cet écran qui ne
          conclut rien, et c'est aussi celle qui porte la photo de l'énoncé —
          l'enfant y dit sur quoi porte l'exercice quand la phrase du cahier de
          textes ne le nomme pas (Hedi, 8 septembre 2026). */}
      <DevoirsDuCahier />

      {/* En fin de semaine seulement (le serveur le dit, `a_demander`) : les
          pages du cahier de classe, matière par matière (lot P5). Ne ferme
          rien, et n'occupe rien tant qu'elle n'a rien à demander ou si sa
          lecture échoue. */}
      <CahierDeLaSemaine />

      {/* Les contrôles des deux semaines et, en terminale, le bac (Hedi,
          29 septembre 2026). Après le cahier de textes, qui est ce qui est dû
          demain ; avant le plan, dont il donne la raison. Chaque compétence
          ouvre l'exercice par le même `commencer` que la liste du soir. */}
      <Echeances commencer={(c) => void commencer(plan, matiere, c)} />

      {plan.repli && !matiere ? (
        <p className="muted small">{explicationDuRepli(plan)}</p>
      ) : null}

      {/* Le jour visé n'a rien donné, et le plan a glissé sur les jours
          d'après. Sans cette carte, l'écran annonce « ce que tu as mardi » et
          liste la semaine suivante — c'est ce que Hedi a vu le 8 septembre
          2026 sur l'écran de Baya. Les phrases sont sans chiffre : un décompte
          de chapitres manquants est un bulletin déguisé (invariant §14.1). */}
      {manque.length > 0 ? (
        <Card title={`Rien à te proposer pour ${plan.le ? enClairCourt(plan.le) : "ce jour-là"}`}>
          <ul>
            {manque.map((raison) => (
              <li key={raison.matiere} className="muted small">
                {phraseDuManque(raison)}
              </li>
            ))}
          </ul>
          <p className="muted small">
            Ce n'est pas de ta faute et ça se répare de notre côté. En attendant, voilà de
            quoi travailler pour les jours qui suivent.
          </p>
        </Card>
      ) : null}

      {etat.quoi === "epuise" ? (
        <Empty>Tu as fait le tour de {etat.sujet}. Choisis autre chose.</Empty>
      ) : null}

      {competences.length === 0 ? (
        <Empty>
          {matiere
            ? "Rien à travailler dans cette matière pour le moment."
            : "Rien à te proposer aujourd'hui. Reviens demain, ou choisis une matière."}
        </Empty>
      ) : (
        <>
          <Liste competences={dabord} commencer={(c) => void commencer(plan, matiere, c)} />
          {ensuite.length > 0 ? (
            <Card title={manque.length > 0 ? "À faire en attendant" : "Si tu as le temps"}>
              {/* Volontairement sans nommer « les jours d'après » : sous le cap
                  « mon prochain contrôle », ces journées-là viennent *avant*
                  l'épreuve visée. Chaque ligne porte sa date, qui est la seule
                  chose vraie dans tous les cas. */}
              <p className="muted small">
                Ce n'est pas pour {plan.le ? enClairCourt(plan.le) : "le jour visé"}. À faire
                quand tu as fini le reste.
              </p>
              <Liste
                competences={ensuite}
                commencer={(c) => void commencer(plan, matiere, c)}
                avecLeJour
              />
              {/* Sans nombre : « 5 chapitres écartés » est un bulletin, et
                  l'invariant §14.1 le refuse. La phrase, elle, dit ce que
                  l'enfant peut faire — en parler dans son journal. */}
              {(plan.sans_preuve ?? 0) > 0 ? (
                <p className="muted small">{phraseSansPreuve(plan.sans_preuve ?? 0)}</p>
              ) : null}
            </Card>
          ) : null}
          {/* Le raccourcissement se dit même quand il ne reste rien à afficher
              en dessous : sans cela, une liste courte se lit comme une panne. */}
          {ensuite.length === 0 && (plan.sans_preuve ?? 0) > 0 ? (
            <p className="muted small">{phraseSansPreuve(plan.sans_preuve ?? 0)}</p>
          ) : null}
        </>
      )}

      <SeSituer />

      <Card title="Travailler une matière">
        <div className="travail__matieres">
          {matiere ? (
            <Button variant="quiet" onClick={() => void charger(null)}>
              ← Revenir au travail du jour
            </Button>
          ) : null}
          {(plan.matieres_principales ?? []).map((nom) => (
            <Button
              key={nom}
              variant={nom === matiere ? "primary" : "quiet"}
              onClick={() => void charger(nom)}
            >
              {etiquette(nom)}
            </Button>
          ))}
        </div>
      </Card>

      <Card title="Mon cap">
        <p className="muted small">
          Ce réglage vaut tous les jours, pas seulement aujourd'hui. Tu peux le changer quand tu
          veux.
        </p>
        <div className="travail__caps">
          {CAPS.map((cap) => (
            <Button
              key={cap.valeur}
              variant={cap.valeur === plan.focus ? "primary" : "quiet"}
              aria-pressed={cap.valeur === plan.focus}
              onClick={() => void changerDeCap(cap.valeur)}
            >
              {cap.libelle}
            </Button>
          ))}
        </div>
      </Card>

      {/* Les objectifs de la semaine — profil lycée seulement. Ils vivaient sur
          l'écran d'accueil, qui n'existe plus : il est devenu le journal. */}
      {profile.weeklyGoals ? <ObjectifsDeLaSemaine /> : null}

      <p className="aside">
        Envie de parler à ton tuteur ? <Link to="/eleve/seance">Ouvre une séance.</Link>
      </p>
    </Screen>
  );
}

/**
 * Ce qui est pour le jour visé, et ce que les jours d'après ont ajouté.
 *
 * La règle vit dans `ui/planDuSoir` : le parent lit la même coupe sur un autre
 * schéma, et deux copies auraient fini par montrer deux plans. Ce qui reste ici
 * est le branchement, exporté pour être testé seul.
 */
export function separer(plan: TravailDuJour): {
  dabord: CompetenceARevoir[];
  ensuite: CompetenceARevoir[];
} {
  return separerParJour(plan.competences ?? [], plan.le);
}

/**
 * Une liste de compétences à travailler.
 *
 * `avecLeJour` n'est pas une décoration : sous « Si tu as le temps », une ligne
 * sans date ne dit pas de quel jour elle vient, et l'enfant ne peut plus la
 * confronter à son emploi du temps — la seule vérification qui lui reste.
 */
function Liste({
  competences,
  commencer,
  avecLeJour = false,
}: {
  competences: CompetenceARevoir[];
  commencer: (c: CompetenceARevoir) => void;
  avecLeJour?: boolean;
}) {
  return (
    <ul className="revision__liste">
      {competences.map((c) => (
        <li key={c.competency_id}>
          <button type="button" className="revision__item" onClick={() => commencer(c)}>
            <span className="revision__matiere">{etiquette(c.matiere)}</span>
            <span className="revision__libelle">
              {c.libelle}
              {avecLeJour && c.pour ? (
                <span className="revision__jour">Pour {enClairCourt(c.pour)}</span>
              ) : null}
              {c.demandee ? (
                <span className="revision__demandee">Cahier de textes : {c.demandee}</span>
              ) : null}
              {!c.demandee && c.prepare ? (
                /* Sans date : l'enfant n'a pas à retenir quand son professeur
                   l'a écrit, il a besoin de savoir pourquoi c'est là. La date
                   reste du côté du parent, qui peut la recouper. */
                <span className="revision__demandee">Pour préparer le prochain cours</span>
              ) : null}
              {!c.demandee && !c.prepare && c.vue ? (
                /* Sans la phrase du professeur ni la date : ce qui compte pour
                   l'enfant est que ce chapitre a été fait en classe, pas d'où
                   nous le savons. Le motif exact reste chez le parent, qui peut
                   le recouper avec le cahier de textes. */
                <span className="revision__demandee">Vu en classe</span>
              ) : null}
            </span>
            <span className="revision__reste">
              {c.disponibles} exercice{c.disponibles > 1 ? "s" : ""}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/** Ce que l'écran sert, dit avant la liste. Exporté pour être testé seul. */
export function annonce(plan: TravailDuJour, matiere: string | null): string {
  if (matiere) return `Tu as choisi ${etiquette(matiere).toLowerCase()}.`;
  const jour = plan.le ? enClairCourt(plan.le) : null;
  if (plan.applique === "examen") {
    return plan.examen
      ? `De quoi préparer ${plan.examen}${jour ? `, ${jour}` : ""}.`
      : "De quoi préparer ton prochain contrôle.";
  }
  if (plan.applique === "demain") {
    return jour ? `Ce que tu as ${jour}.` : "Ce que tu as demain.";
  }
  return "Ce que tu as vu aujourd'hui.";
}

/**
 * Pourquoi ce n'est pas le cap demandé.
 *
 * Le serveur descend toujours vers le cap qui demande le moins de données ; ce
 * qui manquait est donc déductible du cap demandé, et se dit en une phrase.
 */
export function explicationDuRepli(plan: TravailDuJour): string {
  if (plan.focus === "examen") {
    return "Je n'ai pas de contrôle annoncé pour les jours qui viennent, alors je te propose autre chose.";
  }
  if (plan.focus === "demain") {
    return "Je n'ai pas encore ton emploi du temps de demain, alors je te propose ce que tu as vu aujourd'hui.";
  }
  return "Je n'ai pas ta journée d'aujourd'hui, alors je te propose autre chose.";
}

/**
 * Objectifs de la semaine — profil `terminale` seulement.
 *
 * Il les pose, il les voit, personne ne les note. Ils restent sur l'appareil
 * tant que `tutor-engine` n'a pas de place pour eux : un objectif qu'une panne
 * de synchronisation effacerait vaudrait moins que pas d'objectif du tout.
 */
function ObjectifsDeLaSemaine() {
  const key = "tuteur.objectifs";
  const stored = localStorage.getItem(key) ?? "";

  return (
    <Card title="Mes objectifs de la semaine">
      <textarea
        className="textarea"
        defaultValue={stored}
        rows={3}
        aria-label="Objectifs de la semaine"
        onBlur={(event) => localStorage.setItem(key, event.target.value)}
        placeholder="Ce que je veux avoir bouclé dimanche soir."
      />
      <p className="muted small">Sur cet appareil, pour l'instant. Personne d'autre ne les lit.</p>
      <Button variant="quiet" onClick={() => localStorage.removeItem(key)}>
        Effacer
      </Button>
    </Card>
  );
}

/**
 * La porte d'entrée du positionnement, et la seule.
 *
 * **Elle ne s'affiche que tant qu'il reste une matière à situer**, et disparaît
 * ensuite : un test qu'on a passé n'a pas à rester proposé, et une carte qu'on
 * ne peut plus utiliser finit par se lire comme un bouton cassé.
 *
 * Elle vit sur l'écran du travail plutôt que dans la barre d'onglets pour la
 * même raison : c'est un geste qu'on fait une fois par matière, pas un endroit
 * où l'on revient. Une panne ou une absence de réponse n'affiche rien du tout —
 * une proposition facultative ne mérite pas un message d'erreur.
 */
function SeSituer() {
  const [aSituer, setASituer] = useState<EtapeDePositionnement[]>([]);

  useEffect(() => {
    let vivant = true;
    call("positionnementEtat")
      .then((etat) => {
        if (vivant) setASituer((etat.matieres ?? []).filter((etape) => !etape.termine));
      })
      .catch(() => undefined);
    return () => {
      vivant = false;
    };
  }, []);

  if (aSituer.length === 0) return null;

  const commencees = aSituer.some((etape) => (etape.poses ?? 0) > 0);

  return (
    <Card title={commencees ? "Reprendre où tu en es" : "Se situer"}>
      <p className="muted small">
        {commencees
          ? "Tu as commencé à te situer. Quelques questions de plus et je saurai quoi te proposer."
          : `Quelques questions en ${aSituer.map((etape) => etiquette(etape.matiere).toLowerCase()).join(", ")}, pour que je te donne des exercices à ta hauteur. Ce n'est pas noté.`}
      </p>
      <Link className="btn btn--secondary" to="/eleve/positionnement">
        {commencees ? "Continuer" : "Commencer"}
      </Link>
    </Card>
  );
}
